import { httpClient } from "../../../shared/api/httpClient";
import type { PaginatedResponse } from "../../../shared/api/pagination";
import { fetchLineUsersByCustomer } from "../../../entities/line-user/api/lineUsersApi";
import type {
  CreateCustomerManagementRequest,
  CustomerManagementResponse,
  CustomerManagementRow,
  GroupOrderOption,
  UpdateCustomerManagementRequest,
} from "../model/types";

const toRow = (item: CustomerManagementResponse): CustomerManagementRow => ({
  ...item,
  id: `${item.group_id}::${item.line_id}`,
  line_code: item.line_id,
  staff_name: item.representative_name,
  created_at: item.assigned_at,
  group_line_bot: { group_id: item.group_id, group_name: item.group_name },
});

export const fetchCustomerManagement = async (
  page = 1,
  pageSize = 20,
  search?: string,
  status?: string,
  sortBy?: string,
  sortDir?: string,
): Promise<PaginatedResponse<CustomerManagementRow>> => {
  const response = await httpClient.get<
    PaginatedResponse<CustomerManagementResponse>
  >("/customer-management", {
    params: {
      page,
      page_size: pageSize,
      search: search?.trim() || undefined,
      status: status || undefined,
      sort_by: sortBy || undefined,
      sort_dir: sortDir || undefined,
    },
  });
  return { ...response.data, items: response.data.items.map(toRow) };
};

export const fetchAllGroupOrderOptions = async (): Promise<
  GroupOrderOption[]
> => {
  const pageSize = 100;
  let page = 1;
  let totalPages = 1;
  const items: GroupOrderOption[] = [];
  do {
    const response = await httpClient.get<PaginatedResponse<GroupOrderOption>>(
      "/group-orders",
      {
        params: {
          page,
          page_size: pageSize,
          sort_by: "group_name",
          sort_dir: "asc",
        },
      },
    );
    items.push(...response.data.items);
    totalPages = response.data.pagination.total_pages;
    page += 1;
  } while (page <= totalPages);

  return items.filter((group) =>
    group.members?.some((member) => member.status?.toUpperCase() === "PENDING"),
  );
};

export const fetchLinesByCustomer = async (
  mtCode: string,
  branchCode = "",
): Promise<CustomerManagementRow[]> => {
  const lines = await fetchLineUsersByCustomer(mtCode, branchCode);
  return lines.map((line) => ({
    id: line.group_id ? `${line.group_id}::${line.line_id}` : line.line_id,
    group_id: line.group_id || "",
    line_id: line.line_id,
    representative_id: null,
    mt_code: mtCode,
    branch_code: branchCode,
    group_name: line.group_name,
    company_name: null,
    line_name: line.display_name,
    representative_name: line.representative_name,
    phone_number: line.phone,
    status: "",
    assigned_at: "",
    left_at: null,
    delete_at: null,
    customer: null,
    line_code: line.line_id,
    staff_name: line.representative_name || line.display_name,
    created_at: "",
    group_line_bot: {
      group_id: line.group_id || "",
      group_name: line.group_name,
    },
  }));
};

export const createCustomerManagement = async (
  payload: CreateCustomerManagementRequest,
): Promise<CustomerManagementResponse> => {
  const response = await httpClient.post<CustomerManagementResponse>(
    "/customer-management",
    payload,
  );
  return response.data;
};

export const updateCustomerManagement = async (
  groupId: string,
  lineId: string,
  payload: UpdateCustomerManagementRequest,
): Promise<CustomerManagementResponse> => {
  const response = await httpClient.put<CustomerManagementResponse>(
    `/customer-management/${encodeURIComponent(groupId)}/${encodeURIComponent(lineId)}`,
    payload,
  );
  return response.data;
};

export const deleteCustomerManagement = async (
  groupId: string,
  lineId: string,
): Promise<void> => {
  await httpClient.delete(
    `/customer-management/${encodeURIComponent(groupId)}/${encodeURIComponent(lineId)}`,
  );
};
